import { Dispatch, SetStateAction, useState } from 'react';
import { Group, GroupMusicalGenre, deleteGroup } from '../../../features/sessions/sessionGroups/sessionGroups';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import { useParams } from 'react-router-dom';
import { DeleteGroupDialogBox } from '../dialogBox/group/DeleteGroupDialogBox';
import {
    Box, Button, Chip, Dialog, DialogContent, DialogTitle,
    Divider, IconButton, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GroupsIcon from '@mui/icons-material/Groups';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LoginIcon from '@mui/icons-material/Login';

const BG_SECTION     = '#1c1c24';
const BG_CARD        = '#24242e';
const BRAND_RED      = '#c0392b';
const TEXT_PRIMARY   = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.65)';
const BORDER_SUBTLE  = '1px solid rgba(255,255,255,0.08)';

interface GroupDetailsModalProps {
    group: Group | undefined;
    open: Dispatch<SetStateAction<boolean>>;
    enterGroup: () => void;
}

export function GroupDetailsModal({ group, open, enterGroup }: GroupDetailsModalProps) {
    const dispatch    = useDispatch<AppDispatch>();
    const authHeaders = useSelector((state: RootState) => state.session.authHeaders);
    const currentUser = useSelector((state: RootState) => state.session.currentUser);
    const { userNicknameRoute } = useParams();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    function handleFormatDate(date: any) {
        return format(new Date(date), 'dd MMM yyyy');
    }

    async function handleDeleteGroup() {
        if (authHeaders && group?.id) {
            dispatch(deleteGroup({ authHeaders, groupId: `${group.id}` }));
        }
    }

    if (!group) return null;

    const isOwner = currentUser?.nickname === userNicknameRoute;

    return (
        <>
            <Dialog
                open
                maxWidth='sm'
                fullWidth
                onClose={() => open(false)}
                PaperProps={{ sx: { bgcolor: BG_SECTION, border: BORDER_SUBTLE, borderRadius: '12px' } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Typography variant='h6' fontWeight={700} color={TEXT_PRIMARY} noWrap>{group.name}</Typography>
                        <Chip
                            label={group.privacity === 0 ? 'PUBLIC' : 'PRIVATE'}
                            size='small'
                            sx={{
                                bgcolor: group.privacity === 0 ? 'rgba(35,148,0,0.18)' : 'rgba(192,57,43,0.18)',
                                color: group.privacity === 0 ? '#4caf50' : '#e57373',
                                fontWeight: 700, fontSize: '0.65rem',
                            }}
                        />
                    </Box>
                    <IconButton onClick={() => open(false)} size='small' sx={{ color: TEXT_SECONDARY }}>
                        <CloseIcon fontSize='small' />
                    </IconButton>
                </DialogTitle>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />

                <DialogContent sx={{ pt: 2 }}>
                    {/* Description */}
                    {group.description && (
                        <Box sx={{ bgcolor: BG_CARD, borderRadius: '8px', p: 2, mb: 2 }}>
                            <Typography variant='body2' color={TEXT_SECONDARY} sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {group.description}
                            </Typography>
                        </Box>
                    )}

                    {/* Meta info */}
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        <Chip
                            icon={<GroupsIcon sx={{ fontSize: '14px !important' }} />}
                            label={`${group.nMembers} members`}
                            size='small'
                            sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: TEXT_SECONDARY }}
                        />
                        <Chip
                            label={`Created ${handleFormatDate(group.createdAt)}`}
                            size='small'
                            sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: TEXT_SECONDARY }}
                        />
                        <Chip
                            label={`Updated ${handleFormatDate(group.updatedAt)}`}
                            size='small'
                            sx={{ bgcolor: 'rgba(255,255,255,0.06)', color: TEXT_SECONDARY }}
                        />
                    </Box>

                    {/* Musical genres */}
                    {group.groupMusicalGenres?.length > 0 && (
                        <>
                            <Typography variant='overline' color={TEXT_SECONDARY} sx={{ letterSpacing: 2, display: 'block', mb: 1 }}>
                                Musical Genres
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
                                {group.groupMusicalGenres.map((item: GroupMusicalGenre, i: number) => (
                                    <Chip
                                        key={i}
                                        label={item.musicGenre.description}
                                        size='small'
                                        sx={{ bgcolor: 'rgba(255,255,255,0.07)', color: TEXT_PRIMARY }}
                                    />
                                ))}
                            </Box>
                        </>
                    )}

                    {/* Actions */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                        {isOwner && (
                            <Button
                                variant='outlined'
                                size='small'
                                startIcon={<DeleteOutlineIcon />}
                                onClick={() => setDeleteDialogOpen(true)}
                                sx={{
                                    borderColor: 'rgba(192,57,43,0.5)',
                                    color: '#e57373',
                                    textTransform: 'none',
                                    '&:hover': { borderColor: BRAND_RED, bgcolor: 'rgba(192,57,43,0.08)' },
                                }}
                            >
                                Delete
                            </Button>
                        )}
                        <Button
                            variant='contained'
                            size='small'
                            startIcon={<LoginIcon />}
                            onClick={enterGroup}
                            sx={{ bgcolor: BRAND_RED, textTransform: 'none', fontWeight: 600, '&:hover': { bgcolor: '#a93226' } }}
                        >
                            Enter
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

            {deleteDialogOpen && (
                <DeleteGroupDialogBox
                    groupName={group.name}
                    onDeleteGroup={handleDeleteGroup}
                    onCloseDeleteGroupDialogBox={() => setDeleteDialogOpen(false)}
                    onCloseGroupDetailsModal={() => open(false)}
                />
            )}
        </>
    );
}
